import { createSelector } from '@reduxjs/toolkit';
import { INTEGRATIONS } from 'config/integrationsConfig';
import { PRODUCTS } from 'config/productsConfig';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { isCamerasModuleOn } from 'library/helpers/cameraModule';
import { DEFAULT_ORG_TIMEZONE, isValidTimezone, resolveTimezoneFromSettings } from 'library/helpers/timezone';
import { selectEntitledCodes, selectEntitlementGatingActive, isCodeAvailable } from 'reduxStore/billing/selectors';
import {
	selectFeatureEnabled,
	selectFeatureFlagsLoaded,
	selectFeatureFlagsMap,
	resolveFeatureFromMap
} from 'reduxStore/featureFlags/selectors';

//📌 Selector for get all settings from current profile.
export const selectCurrentProfileOrgSettings = createSelector(
	[(state) => state.Settings.mergedOrgSettings, (state) => state.Auth.selectedProfile.orgID],
	(settings, orgID) => settings[orgID] || []
);

//📌 The org's IANA timezone (ORG_TIMEZONE setting, else Australia/Brisbane) — the exact mirror of
// the backend getOrgTimezone(orgID). Safety-critical for Passage Mode: scheduled unlock times must
// be anchored to THIS zone, not the admin's browser, or the door fires at the wrong instant.
export const selectOrgTimezone = createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
	resolveTimezoneFromSettings(orgSettings, CONSTANTS_SETTINGS.ORG_TIMEZONE)
);

/**
 * The ORG_TIMEZONE setting AS READ, or null when the client cannot see it.
 *
 * `selectOrgTimezone` always answers, falling back to Australia/Brisbane — right
 * for computing an instant (it mirrors the backend's own fallback) and wrong for
 * TELLING the user which zone a schedule is armed in: `mergedOrgSettings` is built
 * by mapping over the org DEFAULT-settings catalogue, so a setting missing from
 * that catalogue (or not loaded yet) reads as absent here while the server, which
 * queries OrgSettings directly, sees the real value. Printing the fallback then
 * states a zone the org is not in. Callers that DISPLAY a zone use this and say
 * nothing specific when it is null.
 */
export const selectOrgTimezoneSetting = createSelector([selectCurrentProfileOrgSettings], (orgSettings) => {
	const tz = (orgSettings || []).find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.ORG_TIMEZONE)?.value?.trim();
	return isValidTimezone(tz) ? tz : null;
});

//📌 Selector for the org-settings upsert (write) in-flight flag — used by any settings form's save button.
export const selectUpsertOrgSettingsLoading = (state) => state.Settings.upsertOrgSettingsLoading;

// defaultSettingID → true for org setting rows whose save is in flight.
export const selectLoadingOrgSettingIDs = (state) => state.Settings.loadingOrgSettingIDs;

// A setting group's rows, without internal ones (those are never shown in Settings).
export const selectOrgSettingsByGroupID = createSelector(
	[selectCurrentProfileOrgSettings, (state, groupID) => groupID],
	(orgSettings, groupID) => orgSettings.filter((s) => s.groupID === groupID && !s.isInternal)
);

// Selector for get all person settings from current profile.
export const selectCurrentProfilePersonSettings = createSelector(
	[(state) => state.Settings.mergedPersonSettings, (state) => state.Auth.selectedProfile.personID],
	(settings, personID) => settings[personID] || []
);

// Note: “is Access Module AVAILABLE?” = enabled (flag 82) AND entitled (ACCESS), grandfather-safe.
export const selectHasAccessModule = createSelector(
	[selectCurrentProfileOrgSettings, selectEntitledCodes, selectEntitlementGatingActive],
	(orgSettings, entitledCodes, gatingActive) => {
		const flagOn = orgSettings.some(
			(s) => s.defaultSettingID === CONSTANTS_SETTINGS.ACCESS_MODULE && Number(s.value) === 1
		);
		return flagOn && isCodeAvailable({ catalogCode: 'ACCESS', entitledCodes, gatingActive });
	}
);

//  Note: “is Locker Module AVAILABLE?” = enabled (flag 83) AND entitled (LOCKERS), grandfather-safe.
export const selectHasLockerModule = createSelector(
	[selectCurrentProfileOrgSettings, selectEntitledCodes, selectEntitlementGatingActive],
	(orgSettings, entitledCodes, gatingActive) => {
		const flagOn = orgSettings.some(
			(s) => s.defaultSettingID === CONSTANTS_SETTINGS.LOCKER_MODULE && Number(s.value) === 1
		);
		return flagOn && isCodeAvailable({ catalogCode: 'LOCKERS', entitledCodes, gatingActive });
	}
);

// Note: “is Bookings Module AVAILABLE?” = enabled (flag 160) AND entitled (BOOKINGS), grandfather-safe.
export const selectHasBookingsModule = createSelector(
	[
		selectCurrentProfileOrgSettings,
		selectEntitledCodes,
		selectEntitlementGatingActive,
		// Dev feature flag — the fifth gating axis, evaluated BEFORE the org module setting
		// and the commercial entitlement. Module gate → grandfather-safe default true (never
		// hide an available module while flags load / if the code is unknown).
		(state) => selectFeatureEnabled(state, 'bookings', true)
	],
	(orgSettings, entitledCodes, gatingActive, featureOn) => {
		const settingOn = orgSettings.some(
			(s) => s.defaultSettingID === CONSTANTS_SETTINGS.BOOKINGS_MODULE && Number(s.value) === 1
		);
		return featureOn && settingOn && isCodeAvailable({ catalogCode: 'BOOKINGS', entitledCodes, gatingActive });
	}
);

// settingID → vendor label, for naming WHICH integration is syncing in the top-bar indicator.
// Keep the keys in step with the list `selectSyncInProgress` tests.
const SYNCING_SETTING_LABELS = {
	[CONSTANTS_SETTINGS.IR_SETTING_SYNCING]: 'Integriti',
	[CONSTANTS_SETTINGS.CONTROL_SETTING_SYNCING]: 'Zezamii Lock',
	[CONSTANTS_SETTINGS.INCEPTION_SETTING_SYNCING]: 'Inception',
	[CONSTANTS_SETTINGS.TTLOCK_SETTING_SYNCING]: 'TTLock'
};

// Names of the integrations whose syncing flag is currently 1 — [] when nothing is syncing.
export const selectSyncingIntegrations = createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
	Object.entries(SYNCING_SETTING_LABELS)
		.filter(([settingID]) => orgSettings.find((s) => s.defaultSettingID === Number(settingID))?.value === '1')
		.map(([, label]) => label)
);

// Generic sync status selector - returns true if ANY integration is syncing (IR, Control or Inception)
export const selectSyncInProgress = createSelector(
	[selectSyncingIntegrations],
	(syncingIntegrations) => syncingIntegrations.length > 0
);

// Legacy selector for IR sync (for backwards compatibility)
export const selectIRSyncInProgress = createSelector(
	[selectCurrentProfileOrgSettings],
	(orgSettings) =>
		orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.IR_SETTING_SYNCING)?.value === '1'
);

// Control sync status selector
export const selectControlSyncInProgress = createSelector(
	[selectCurrentProfileOrgSettings],
	(orgSettings) =>
		orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CONTROL_SETTING_SYNCING)?.value === '1'
);

// TTLock sync status selector
export const selectTTLockSyncInProgress = createSelector(
	[selectCurrentProfileOrgSettings],
	(orgSettings) =>
		orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.TTLOCK_SETTING_SYNCING)?.value === '1'
);

export const selectIsModuleEnabled = (defaultSettingID) =>
	createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
		orgSettings.some((s) => s.defaultSettingID === defaultSettingID && Number(s.value) === 1)
	);

/**
 * Have this org's settings actually arrived?
 *
 * 🛑 Every `selectIsModuleEnabled`-style flag reads FALSE before the fetch lands, so "the
 * module is off" and "we have not asked yet" are the same value. A page that turns a module
 * flag into a locked door has to tell them apart or it locks the door on every hard refresh
 * for as long as the settings request is in flight.
 *
 * A non-empty array counts as loaded on purpose: `orgSettingsLoadingComplete` is set by the
 * fetch, and settings also arrive merged with the profile, so the flag can still be false
 * while the rows are already in the store.
 */
export const selectOrgSettingsLoaded = createSelector(
	[(state) => state.Settings.orgSettingsLoadingComplete === true, selectCurrentProfileOrgSettings],
	(complete, orgSettings) => complete || orgSettings.length > 0
);

// Inception behaves like Inner Range (IR) for credentials: both are Inner Range access-control
// systems that require a card number + site-code template. Anywhere the UI gated card-template
// behaviour on IR alone, gate it on (IR OR Inception) via this selector so both look the same.
export const selectIsAcsCardTemplateEnabled = createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
	orgSettings.some(
		(s) =>
			(s.defaultSettingID === CONSTANTS_SETTINGS.INNER_RANGE_MODULE ||
				s.defaultSettingID === CONSTANTS_SETTINGS.INCEPTION_MODULE) &&
			Number(s.value) === 1
	)
);

// Inception has no access schedules (unlike IR): permission UIs drop their Schedule column, grants stay
// permanent and Open Access shows devices only. Assumes IR and Inception never share an org.
export const selectIsInceptionEnabled = selectIsModuleEnabled(CONSTANTS_SETTINGS.INCEPTION_MODULE);

// Authoring-only, not a module gate: may this org CREATE a new org-wide (global) guest type?
// Default off — most orgs run one site and the global/site choice is pure confusion for them.
// Does not affect an existing global type: it still shows in the list and stays editable
// regardless of this flag (see docs/plans/visitor-type-scoping/PLAN.md, platform side).
export const selectAllowsGlobalVisitorTypes = selectIsModuleEnabled(CONSTANTS_SETTINGS.VISITOR_TYPE_GLOBAL_SCOPE);

// The Cameras surface (page + sidebar item) only makes sense when a camera provider is
// active — Verkada or Vortex/Vivotek. Mirrors selectIsAcsCardTemplateEnabled's OR pattern.
// Through the shared predicate — app/selectors needs the same answer and cannot import
// this module (it would drag billing and firebase into the core readiness graph, which
// broke two selector suites at import time). See library/helpers/cameraModule.
export const selectIsCamerasModuleEnabled = createSelector([selectCurrentProfileOrgSettings], (orgSettings) =>
	isCamerasModuleOn(orgSettings)
);

// Use this when you have a list of things each with a `moduleFlag` and need
// "the subset that's active". For a single flag, use selectIsModuleEnabled.
//
// Module availability = Enabled (flag) AND Entitled (billing). Entitlement is composed here so
// the switcher, sidebar, and route visibility (all driven off selectActiveProducts) inherit it —
// not just the Products page. Gating is grandfather-safe (see Redux/billing/selectors.js): orgs
// not yet on billing are governed by the flag alone, exactly as before.
export const selectActiveModules = (modules) =>
	createSelector(
		[
			selectCurrentProfileOrgSettings,
			selectEntitledCodes,
			selectEntitlementGatingActive,
			selectFeatureFlagsLoaded,
			selectFeatureFlagsMap
		],
		(orgSettings, entitledCodes, gatingActive, ffLoaded, ffMap) =>
			modules.filter((m) => {
				const row = orgSettings.find((s) => s.defaultSettingID === m.moduleFlag);
				const settingOn = Number(row?.value) === 1;
				// Entitlement: most products gate on a single `catalogCode`; a tiered product may
				// declare `catalogCodesAny` and is entitled when ANY tier is available — e.g. Vision
				// appears for orgs on either Vision AI · Cameras OR Vision AI · Hub.
				const entitled = Array.isArray(m.catalogCodesAny)
					? m.catalogCodesAny.some((code) =>
							isCodeAvailable({ catalogCode: code, entitledCodes, gatingActive })
						)
					: isCodeAvailable({ catalogCode: m.catalogCode, entitledCodes, gatingActive });
				// Dev feature flag by product key (grandfather-safe default true → a module with
				// no seeded flag, e.g. access/lockers/rooms/vision, is never hidden; only seeded flags
				// like 'bookings' gate). This makes the dark launch cover the switcher/sidebar/
				// route, not just the DashboardChooser card.
				const featureOn = resolveFeatureFromMap(ffMap, ffLoaded, m.key, true);
				return featureOn && settingOn && entitled;
			})
	);

// PERMISSION_MODE (setting 139) is retired — schedule + expiry always coexist.
// Selectors selectPermissionMode / selectIsPermissionMode removed accordingly
// (see docs/features/unified-schedule-expiry). CONSTANTS_SETTINGS.PERMISSION_MODE
// is kept only for the settings container until the backend setting is dropped.

// ── Cameras page polling behaviour ──────────────────────────────────────────
// Thumbnail refresh cadence in ms. Org-configurable via setting 156; clamped
// to 3–60 s so a typo can't hammer the camera-provider APIs (or freeze tiles).
export const selectCameraThumbnailRefreshMs = createSelector([selectCurrentProfileOrgSettings], (orgSettings) => {
	const seconds = Number(
		orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CAMERA_THUMBNAIL_REFRESH_SECONDS)?.value
	);
	const clamped = Number.isFinite(seconds) && seconds > 0 ? Math.min(Math.max(seconds, 3), 60) : 5;
	return clamped * 1000;
});

// Idle pause in ms. Org-configurable via setting 157; 0 (or unset-to-0)
// disables the pause entirely — wall-mounted camera displays need polling to
// continue with nobody at the keyboard. Default 60 s.
export const selectCameraIdlePauseMs = createSelector([selectCurrentProfileOrgSettings], (orgSettings) => {
	const raw = orgSettings.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CAMERA_IDLE_PAUSE_SECONDS)?.value;
	const seconds = Number(raw);
	if (raw !== undefined && raw !== null && Number.isFinite(seconds)) {
		return seconds <= 0 ? 0 : Math.max(seconds, 15) * 1000;
	}
	return 60 * 1000;
});

// ── Concrete selectors ──────────────────────────────────────────────────────
export const selectActiveProducts = selectActiveModules(PRODUCTS);
export const selectActiveIntegrations = selectActiveModules(INTEGRATIONS);

// "is Vision AVAILABLE?" — derived from the composed product gating so the DashboardChooser card
// inherits the exact same axes (camera flag any-of + CAMERAS entitlement + dark-launch flag) as the
// switcher/sidebar, with no second copy of the rules to drift.
export const selectHasVisionModule = createSelector([selectActiveProducts], (products) =>
	products.some((p) => p.key === 'vision')
);

// Note: "is Visitor Management Module AVAILABLE?" = enabled (setting 223) AND entitled
// (VISITOR_MANAGEMENT) AND flag ON — same shape as selectHasBookingsModule above, EXCEPT the flag
// fallback is `false`, not `true`. `visitorManagement` is still mid dark-launch, so a
// loading/unregistered flag must fail CLOSED (never flash the VMS tabs), unlike an established
// module's grandfather-safe default. Gates the Visitor Hub's VMS-only sections (Visits / Visitor
// Types / Devices / Settings — Directory stays Access-owned and always on, never gated here) and the
// TopBar tablet-preview icon. See docs/features/visitor-management/vms-module-productization.md.
export const selectHasVmsModule = createSelector(
	[
		selectCurrentProfileOrgSettings,
		selectEntitledCodes,
		selectEntitlementGatingActive,
		(state) => selectFeatureEnabled(state, 'visitorManagement', false)
	],
	(orgSettings, entitledCodes, gatingActive, featureOn) => {
		const settingOn = orgSettings.some(
			(s) => s.defaultSettingID === CONSTANTS_SETTINGS.VISITOR_MANAGEMENT_MODULE && Number(s.value) === 1
		);
		return (
			featureOn &&
			settingOn &&
			isCodeAvailable({ catalogCode: 'VISITOR_MANAGEMENT', entitledCodes, gatingActive })
		);
	}
);

/** The org's zone for entering a stay or an access period (stays are stored in the same one); the platform
 * default before settings load. */
export const selectStayTimeZone = (state) =>
	state.Settings?.mergedOrgSettings && state.Auth?.selectedProfile ? selectOrgTimezone(state) : DEFAULT_ORG_TIMEZONE;
